import { supabase } from "@/integrations/supabase/client";
import { connectorActionError } from "@/lib/connectorActionError";
import { createMirrorAuthSessionScope, mirrorAuthIdentity } from "@/lib/mirrorAuthSession";
export interface EmailSessionGuard { authSessionId: string; isCurrent: () => boolean }
/** Captured JWT and session lease; observations cannot adopt a different caller. */
export async function emailSessionRequest(ownerId: string, body: Record<string, unknown>,
  guard: EmailSessionGuard, surfaceQuota: (cause: unknown) => Promise<boolean> = async () => false) {
  const scope = createMirrorAuthSessionScope();
  const assertGuard = () => { if (!guard.isCurrent()) throw new Error("stale_email_request"); };
  assertGuard();
  const original = await supabase.auth.getSession();
  assertGuard();
  const identity = mirrorAuthIdentity(original.data.session);
  if (original.error || !identity || identity.ownerId !== ownerId || identity.authSessionId !== guard.authSessionId) throw new Error("invalid_session");
  scope.observeSession(original.data.session);
  const lease = scope.capture()!;
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => scope.observeSession(session));
  const assert = () => { assertGuard(); if (!scope.isCurrent(lease)) throw new Error("stale_email_request"); };
  const check = async () => {
    assert(); const active = await supabase.auth.getSession(); assert();
    if (active.error || !scope.matchesSession(active.data.session, lease)) throw new Error("stale_email_request");
  };
  try {
    await check();
    const response = await supabase.functions.invoke("email-executor", {
      body: { ...body, auth_session_id: lease.authSessionId }, headers: { Authorization: "Bearer " + lease.accessToken },
    });
    await check();
    if (response.error) {
      if (await surfaceQuota(response.error)) { assert(); throw new Error("__quota_surfaced__"); }
      assert(); const parsed = await connectorActionError(response.error); assert(); throw parsed;
    }
    if (response.data?.error) throw new Error(String(response.data.error));
    assert(); return response.data;
  } finally { scope.invalidate(); subscription.unsubscribe(); }
}
